import publicApi from '../../lib/publicApi';
import baseApi from '../../lib/baseApi';
import uploadApi from "@/lib/uploadApi";
import { FeedbackFormData, PrayerFormData, VideoSearchParams, TermsAndPolicy, VideoCategory, VideoSeries, OfficialVideo } from "../../types/main/mainTypes";
import { logger } from "../../utils/logging/logger";



// Terms and Policies API interaction ----------------------------------------
export const fetchTermsAndPolicies = async (): Promise<TermsAndPolicy[]> => {
  const { data } = await publicApi.get('/terms-and-policies/');
  return Array.isArray(data) ? data
       : Array.isArray((data as any)?.results) ? (data as any).results
       : [];
};

// Accept Policy API interaction ---------------------------------------------
export const acceptPolicy = async (policyId: number) => {
  const { data } = await baseApi.post("/user-agreements/", {
    policy: policyId,
  });
  return data;
};

// Static Choice API interaction ---------------------------------------------
export const fetchStaticChoice = async (
  choiceName: string,
  params?: Record<string, string | number | boolean | undefined>
) => {
  try {
    const token = localStorage.getItem("access_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // build query string safely
    const qs = params
      ? "?" +
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== null && v !== "")
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join("&")
      : "";

    const response = await publicApi.get(`/static-choice/${choiceName}/${qs}`, { headers });
    return response.data; // Expect: [ [value,label], ... ]
  } catch (error: any) {
    throw error;
  }
};


// Feedback API interaction ---------------------------------------------------
export const submitUserFeedback = async (data: FeedbackFormData) => {
  const formData = new FormData();
  formData.append("title", data.title);
  formData.append("content", data.content);
  if (data.screenshot) {
    formData.append("screenshot", data.screenshot);
  }

  const response = await uploadApi.post("/user-feedbacks/submit-feedback/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data
};

// Submit Prayer Request (POST) -----------------------------------------------------------
export const submitPrayerRequest = async (data: PrayerFormData) => {
  const response = await baseApi.post("/prayers/", data);
  return response.data;
};

// Fetch Public Prayers with pagination 
export const fetchPublicPrayers = async (page = 1) => {
  const response = await publicApi.get(`/prayers/?page=${page}`);
  return response.data;
};

export const fetchTotalPrayerCount = async () => {
  const response = await publicApi.get('/prayers/total-count/');
  return response.data.total;
};


// -------------------------------------------------------------------------------------------------------------------------------------
function normalizeArray<T>(data: any): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && Array.isArray((data as any).results)) return (data as any).results as T[];
  return [];
}

// Fetch Video Categories API interaction ---------------------------------------------------
export const fetchVideoCategories = async (): Promise<VideoCategory[]> => {
  const { data } = await publicApi.get('/video-categories/');
  return normalizeArray<VideoCategory>(data);
};
// Fetch Video Series API interaction -------------------------------------------------------
export const fetchVideoSeries = async (): Promise<VideoSeries[]> => {
  const { data } = await publicApi.get('/video-series/');
  return normalizeArray<VideoSeries>(data);
};

// Fetch Official Video API interaction ------------------------------------------------------
export const fetchOfficialVideos = async (): Promise<OfficialVideo[]> => {
  const { data } = await publicApi.get('/official-videos/');
  return normalizeArray<OfficialVideo>(data);
};

// Register Video View API interaction ------------------------------------------------------
export const registerVideoView = async (videoId: number) => {
  try {
    const response = await publicApi.post(
      `/official-videos/${videoId}/track_view/`
    );
    return response.data;
  } catch (error: any) {
    // Non-invasive logging
    logger.error("Error tracking video view", {
      videoId,
      status: error?.response?.status,
      error: error?.message,
    });
    throw error;
  }
};


// Search and Filter Official Videos -----------------------------------------------------------
export const searchOfficialVideos = async (params: VideoSearchParams) => {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      query.append(key, value.toString());
    }
  });

  const response = await publicApi.get(`/official-videos/?${query.toString()}`);
  return response.data;
};
